import { DatePicker, useDatePickerContext } from "@ark-ui/react";
import { mdiChevronLeft, mdiChevronRight } from "@mdi/js";
import Icon from "@mdi/react";
import clsx from "clsx";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import { useDatePickerCustomContext } from "../DatePickerContext";
import { useEffect, useRef } from "react";

dayjs.extend(isBetween);

type Props = {
  onViewChange?: (visibleRange: { year: number; month: number }) => void;
};

const DayView = ({ onViewChange }: Props) => {
  const { color, mode } = useDatePickerCustomContext();
  const datePicker = useDatePickerContext();
  const prevVisibleRangeRef = useRef<{ year: number; month: number } | null>(null);

  const isRangeMode = mode === "range";

  const rangeStartColor =
    color === "primary"
      ? "bg-[linear-gradient(to_right,transparent_50%,#e9d6fa_50%)]"
      : "bg-[linear-gradient(to_right,transparent_50%,#D6DFFA_50%)]";
  const rangeEndColor =
    color === "primary"
      ? "bg-[linear-gradient(to_right,#e9d6fa_50%,transparent_50%)]"
      : "bg-[linear-gradient(to_right,#D6DFFA_50%,transparent_50%)]";

  const rangeBetweenColor = color === "primary" ? "bg-primary-100" : "bg-blue-100";

  const activeColor = color === "primary" ? "bg-primary-500 text-white" : "bg-blue-500 text-white";

  const hoverColor = color === "primary" ? "hover:bg-primary-10" : "hover:bg-blue-10";

  const isOpen = datePicker.open;
  const currentYear = datePicker.visibleRange?.start?.year;
  const currentMonth = datePicker.visibleRange?.start?.month;
  const currentView = datePicker.view;

  useEffect(() => {
    if (onViewChange && isOpen && currentYear && currentMonth && currentView === "day") {
      const currentVisibleRange = {
        year: currentYear,
        month: currentMonth,
      };

      const prevRange = prevVisibleRangeRef.current;

      if (!prevRange || prevRange.year !== currentVisibleRange.year || prevRange.month !== currentVisibleRange.month) {
        prevVisibleRangeRef.current = currentVisibleRange;
        onViewChange(currentVisibleRange);
      }
    }
  }, [isOpen, currentYear, currentMonth, currentView, onViewChange]);

  return (
    <DatePicker.View view="day">
      <DatePicker.ViewControl className="flex justify-between items-center py-2">
        <DatePicker.PrevTrigger>
          <Icon path={mdiChevronLeft} size="24px" />
        </DatePicker.PrevTrigger>
        <DatePicker.ViewTrigger>
          <span className="text-subtitle4">{datePicker.visibleRangeText.start.replace(" ", ", ")}</span>
        </DatePicker.ViewTrigger>
        <DatePicker.NextTrigger>
          <Icon path={mdiChevronRight} size="24px" />
        </DatePicker.NextTrigger>
      </DatePicker.ViewControl>

      <DatePicker.Table className="mt-4">
        <DatePicker.TableHead>
          <DatePicker.TableRow>
            {datePicker.weekDays.map((weekDay, idx) => (
              <DatePicker.TableHeader
                key={weekDay.long}
                className={clsx(
                  "w-10 text-label3 text-center font-semibold py-2 leading-none",
                  idx === 0 && "text-red-500",
                  idx === 6 && "text-blue-500",
                )}
              >
                {weekDay.short}
              </DatePicker.TableHeader>
            ))}
          </DatePicker.TableRow>
        </DatePicker.TableHead>
        <DatePicker.TableBody>
          {datePicker.weeks.map((week, idx) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
            <DatePicker.TableRow key={idx} className="mt-3">
              {week.map((day, idx) => {
                const selected = datePicker.valueAsDate.some(
                  (date) =>
                    date.getFullYear() === day.year && date.getMonth() + 1 === day.month && date.getDate() === day.day,
                );

                const isBetweenRange =
                  isRangeMode &&
                  datePicker.valueAsDate.length === 2 &&
                  dayjs(new Date(day.year, day.month - 1, day.day)).isBetween(
                    dayjs(datePicker.valueAsDate[0]),
                    dayjs(datePicker.valueAsDate[1]),
                    "day",
                  );

                const isRangeStart =
                  isRangeMode &&
                  datePicker.valueAsDate.length === 2 &&
                  dayjs(new Date(day.year, day.month - 1, day.day)).isSame(dayjs(datePicker.valueAsDate[0]), "day");

                const isRangeEnd =
                  isRangeMode &&
                  datePicker.valueAsDate.length === 2 &&
                  dayjs(new Date(day.year, day.month - 1, day.day)).isSame(dayjs(datePicker.valueAsDate[1]), "day");

                return (
                  <DatePicker.TableCell
                    // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
                    key={idx}
                    value={day}
                    className={clsx(
                      "p-0",
                      isBetweenRange && rangeBetweenColor,
                      isRangeStart && rangeStartColor,
                      isRangeEnd && rangeEndColor,
                    )}
                  >
                    <DatePicker.TableCellTrigger asChild>
                      <button
                        type="button"
                        className={clsx(
                          "w-10 h-10 rounded-full text-label2",
                          selected && activeColor,
                          "[&:is([data-disabled])]:text-gray-300 [&:is([data-disabled])]:cursor-not-allowed",
                          !selected && !isBetweenRange && !isRangeStart && !isRangeEnd && hoverColor,
                        )}
                      >
                        {day.day}
                      </button>
                    </DatePicker.TableCellTrigger>
                  </DatePicker.TableCell>
                );
              })}
            </DatePicker.TableRow>
          ))}
        </DatePicker.TableBody>
      </DatePicker.Table>
    </DatePicker.View>
  );
};

export default DayView;
